import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {Course} from '../../models/Course';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import { PencilSquare } from 'react-bootstrap-icons';
import { DataTableColumn } from '../widget/DataTable';
import DataTable from '../widget/DataTable';
import { CourseModal } from './modal/CourseModal';
import { getData } from '../../utils/GetData';

import { RichTextDisplay } from '../widget/RichText';
import { sharedSearchCache } from '../../../api/SearchCache';
import { SessionContext } from '../../context/SessionContext';

export const CourseList = () => {
    const [courses, setCourses] = useState<Course[] | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState<{ column: string | null; direction: "asc" | "desc" | "none" }>({ column: null, direction: "asc" });
    const [pageSize, setPageSize] = useState(5);
    const [searchError, setSearchError] = useState<string | null>(null);

    const [selectedCourse, setSelectedCourse] = useState<Course | undefined>(undefined);
    const [showModal, setShowModal] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [needsReload, setNeedsReload] = useState(true);

    const sessionInfo = useContext(SessionContext);
    const sessionUser = sessionInfo?.user ? sessionInfo.user : null;

    const cols= useMemo<DataTableColumn<Course>[]>(() => {
        const columns: DataTableColumn<Course>[] = [
            {column: "course_id", label: "ID", headerClassName: "sortable-header", cellClassName: "align-middle", sortable: true, headerStyle: { width: "5%" }},
            {column: "title", label: "Title", headerClassName: "sortable-header", cellClassName: "align-middle", sortable: true, headerStyle:{width: "30%"}},
            {column: "description", label: "Description", headerStyle: { width: "57%" },
                accessor: (row:any) => (
                    <Accordion flush className="course-description-accordion" style={{margin:'0'}}>
                        <Accordion.Item eventKey='0'>
                            <Accordion.Header>Click to View</Accordion.Header>
                            <Accordion.Body className="readonly-description">
                                <RichTextDisplay value={row.description} />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )
            }
        ];

        if(sessionUser && sessionUser.user_role === "Admin") {
            columns.push(
                {column: "edit", label: "Edit", cellClassName:"align-middle", headerStyle: { width: "8%" },
                    accessor: (row:Course) => (
                        <Button 
                            className="text-light" 
                            onClick={() => {
                                setIsNew(false);
                                setSelectedCourse(row); 
                                setShowModal(true);
                            }}
                            aria-label="Edit"
                        >
                            <PencilSquare/>
                        </Button>
                    )
                }
            );
        }

        return columns;

    }, [courses]);

    const getCourses = async () => {
        const result = await getData<Course>(
            '/api/course/search', 
            page, 
            pageSize,
            {
                column: sort.column,
                direction: sort.direction
            }
        );

        if(typeof result === 'object') {
            setCourses(result.data);
            setTotal(result.total);
            setSearchError(null);
        } else {
            setSearchError(result);
        }
    };
    
    useEffect(() => {
        setNeedsReload(true);
    }, [page, sort, pageSize]);

    useEffect(() => {
        if(needsReload) {
            getCourses();
            setNeedsReload(false);
        }
    }, [needsReload]);

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedCourse(undefined);
    };

    const initiateReload = useCallback(() => {
        sharedSearchCache.clear();
        setTimeout(() => {
            setNeedsReload(true);
        }, 1);
    }, [setNeedsReload]);

    const handleSort = (col: DataTableColumn<Course>) => {
        if (!col.sortable) return;
        let direction: "asc" | "desc" | "none" = "asc";
        if (sort.column === col.column) {
            if (sort.direction === "asc") {
                direction = "desc";
            } else if (sort.direction === "desc") {
                direction = "none";
            } else {
                direction = "asc";
            }
        }
        const newSort = { column: direction === "none" ? null : col.column, direction };
        setSort(newSort);
        setPage(1);
        initiateReload();
    };
    
    return (
        
        <div>
            {selectedCourse !== undefined &&
                <CourseModal 
                    show={showModal} 
                    onHide={handleModalClose} 
                    course={selectedCourse} 
                    isNew={isNew} 
                    initiateReload={initiateReload}
                />
            }
            <h1 className="text-light">Course List</h1>
            <h2 className="text-light" style={{fontSize: '1rem'}}>For searching for and building reusable courses.</h2>
            {sessionUser && sessionUser.user_role === 'Admin' && 
                <Button 
                    variant="success" 
                    style={{boxShadow:'0 10px 12px 0 rgba(0, 0, 0, 0.25)'}}
                    onClick={() => {
                        setIsNew(true);
                        setSelectedCourse({course_id: 0, title: '', description: ''});
                        setShowModal(true);
                    }}
                >
                    Add Course
                </Button>
            }
            
            <hr className="border"/>

            <div style={{padding:"1em", backgroundColor:"white", borderRadius:"0.5em"}}>
                {searchError &&
                    <div>
                        <strong>Search error:</strong> {searchError}
                    </div>
                }
                {courses &&
                    <DataTable                    
                        tableClassName="table-stack-xs course-list-table"
                        tableStyle={{ tableLayout: "fixed", width: "100%" }}
                        responsive={false}
                        data={courses}
                        columns={cols}
                        id="course-list"
                        page={page}
                        pageSize={pageSize}
                        totalRecords={total}
                        onPageChange={setPage}
                        sort={sort}
                        handlePageSizeChange={(newPageSize: number) => {
                            setPageSize(newPageSize);
                            setPage(1);  // Reset to first page when changing items per page
                        }}
                        onSort={handleSort}
                    />
                }
            </div>
        </div>
    );
}